import { instance } from "api/apiBase";
import {
  ShowingType,
  SchedulingSettings,
  UpdateSchedulingSettingsPayload,
  ShowingSettings,
  UpdateShowingSettingsPayload,
  AvailabilityRule,
  ReplaceAvailabilityPayload,
  BlockedDay,
  AddBlockedDayPayload,
  DateOverride,
  SetDateOverridePayload,
  RemoveDateOverridePayload,
  CalendarParams,
  CalendarResponse,
  Showing,
  ShowingDetail,
  ShowingNote,
  ShowingRotationSlot,
  ReserveSlotPayload,
  BookFromReservationPayload,
  BookDirectPayload,
  CancelShowingPayload,
  RescheduleShowingPayload,
  AddFeedbackPayload,
  ReassignShowingPayload,
  AddNotePayload,
  AvailabilitySlot,
  AvailabilitySlotParams,
  AvailabilityOverviewParams,
  AvailabilityOverviewResponse,
  ListShowingsParams,
  ListShowingsResponse,
  LeadShowingsResponse
} from "./types";

const buildShowingsQuery = (params: ListShowingsParams): string => {
  // eslint-disable-next-line compat/compat
  const sp = new URLSearchParams();

  if (params.property_id) sp.append("property_id", String(params.property_id));
  if (params.unit_id) sp.append("unit_id", String(params.unit_id));
  if (params.partner_user_id)
    sp.append("partner_user_id", String(params.partner_user_id));
  params.status?.forEach((s) => sp.append("status[]", s));
  if (params.start_date) sp.append("start_date", params.start_date);
  if (params.end_date) sp.append("end_date", params.end_date);
  if (params.page) sp.append("page", String(params.page));
  if (params.per_page) sp.append("per_page", String(params.per_page));

  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

export const schedulingApi = {
  // --- Account Scheduling Settings ---

  getSchedulingSettings: async (
    showingType: ShowingType
  ): Promise<SchedulingSettings> => {
    return (await instance.get(`/portal/v1/scheduling_settings/${showingType}`))
      .data;
  },

  updateSchedulingSettings: async (
    showingType: ShowingType,
    data: UpdateSchedulingSettingsPayload
  ): Promise<SchedulingSettings> => {
    return (
      await instance.put(`/portal/v1/scheduling_settings/${showingType}`, data)
    ).data;
  },

  // --- User Showing Settings ---

  getShowingSettings: async (): Promise<ShowingSettings> => {
    return (await instance.get(`/portal/v1/my/showing_settings`)).data;
  },

  updateShowingSettings: async (
    data: UpdateShowingSettingsPayload
  ): Promise<ShowingSettings> => {
    return (await instance.put(`/portal/v1/my/showing_settings`, data)).data;
  },

  // --- Weekly Availability ---

  getAvailability: async (): Promise<AvailabilityRule[]> => {
    return (await instance.get(`/portal/v1/my/availability`)).data;
  },

  replaceAvailability: async (
    data: ReplaceAvailabilityPayload
  ): Promise<AvailabilityRule[]> => {
    return (await instance.put(`/portal/v1/my/availability`, data)).data;
  },

  // --- Blocked Days ---

  listBlockedDays: async (): Promise<BlockedDay[]> => {
    return (await instance.get(`/portal/v1/my/blocked_days`)).data;
  },

  addBlockedDay: async (data: AddBlockedDayPayload): Promise<BlockedDay> => {
    return (await instance.post(`/portal/v1/my/blocked_days`, data)).data;
  },

  removeBlockedDay: async (blockedDayId: number): Promise<void> => {
    await instance.delete(`/portal/v1/my/blocked_days/${blockedDayId}`);
  },

  // --- Date Overrides ---

  listDateOverrides: async (): Promise<DateOverride[]> => {
    return (await instance.get(`/portal/v1/my/date_overrides`)).data;
  },

  setDateOverride: async (
    data: SetDateOverridePayload
  ): Promise<DateOverride[]> => {
    return (await instance.put(`/portal/v1/my/date_overrides`, data)).data;
  },

  removeDateOverride: async (
    data: RemoveDateOverridePayload
  ): Promise<void> => {
    await instance.delete(`/portal/v1/my/date_overrides`, { data });
  },

  // --- Calendar ---

  getCalendar: async (params: CalendarParams): Promise<CalendarResponse> => {
    const parts: string[] = [
      `start_date=${encodeURIComponent(params.start_date)}`,
      `end_date=${encodeURIComponent(params.end_date)}`
    ];
    if (params.timezone)
      parts.push(`timezone=${encodeURIComponent(params.timezone)}`);
    params.property_ids?.forEach((id) => parts.push(`property_ids[]=${id}`));
    params.unit_ids?.forEach((id) => parts.push(`unit_ids[]=${id}`));
    params.partner_user_ids?.forEach((id) =>
      parts.push(`partner_user_ids[]=${id}`)
    );
    params.statuses?.forEach((s) =>
      parts.push(`statuses[]=${encodeURIComponent(s)}`)
    );
    params.showing_types?.forEach((t) =>
      parts.push(`showing_types[]=${encodeURIComponent(t)}`)
    );
    return (
      await instance.get(`/portal/v1/showings/calendar?${parts.join("&")}`)
    ).data;
  },

  // --- Availability Slots ---

  getAvailabilitySlots: async (
    params: AvailabilitySlotParams
  ): Promise<AvailabilitySlot[]> => {
    // eslint-disable-next-line compat/compat
    const sp = new URLSearchParams();
    sp.append("property_id", String(params.property_id));
    if (params.unit_id) sp.append("unit_id", String(params.unit_id));
    if (params.lead_id) sp.append("lead_id", String(params.lead_id));
    if (params.partner_user_id)
      sp.append("partner_user_id", String(params.partner_user_id));
    if (params.showing_type) sp.append("showing_type", params.showing_type);
    return (
      await instance.get(`/portal/v1/showings/availability?${sp.toString()}`)
    ).data;
  },

  // --- Availability Overview (PX unit "Showing availability" tab) ---

  getAvailabilityOverview: async (
    params: AvailabilityOverviewParams
  ): Promise<AvailabilityOverviewResponse> => {
    // eslint-disable-next-line compat/compat
    const sp = new URLSearchParams();
    sp.append("property_id", String(params.property_id));
    sp.append("unit_id", String(params.unit_id));
    sp.append("start_date", params.start_date);
    sp.append("end_date", params.end_date);
    return (
      await instance.get(
        `/portal/v1/showings/availability_overview?${sp.toString()}`
      )
    ).data;
  },

  // --- Showings ---

  reserveSlot: async (data: ReserveSlotPayload): Promise<Showing> => {
    return (await instance.post(`/portal/v1/showings/reserve`, data)).data;
  },

  bookFromReservation: async (
    data: BookFromReservationPayload
  ): Promise<Showing> => {
    return (await instance.post(`/portal/v1/showings/book`, data)).data;
  },

  bookDirect: async (data: BookDirectPayload): Promise<Showing> => {
    return (await instance.post(`/portal/v1/showings/book`, data)).data;
  },

  listShowings: async (
    params: ListShowingsParams
  ): Promise<ListShowingsResponse> => {
    const qs = buildShowingsQuery(params);
    return (await instance.get(`/portal/v1/showings${qs}`)).data;
  },

  listLeadShowings: async (
    leadId: number | string
  ): Promise<LeadShowingsResponse> => {
    return (await instance.get(`/portal/v1/leads/${leadId}/showings`)).data;
  },

  getShowing: async (showingId: number): Promise<ShowingDetail> => {
    return (await instance.get(`/portal/v1/showings/${showingId}`)).data;
  },

  confirmShowing: async (showingId: number): Promise<Showing> => {
    return (await instance.post(`/portal/v1/showings/${showingId}/confirm`))
      .data;
  },

  cancelShowing: async (
    showingId: number,
    data?: CancelShowingPayload
  ): Promise<Showing> => {
    return (
      await instance.post(`/portal/v1/showings/${showingId}/cancel`, data)
    ).data;
  },

  rescheduleShowing: async (
    showingId: number,
    data: RescheduleShowingPayload
  ): Promise<Showing> => {
    return (
      await instance.post(`/portal/v1/showings/${showingId}/reschedule`, data)
    ).data;
  },

  completeShowing: async (showingId: number): Promise<Showing> => {
    return (await instance.post(`/portal/v1/showings/${showingId}/complete`))
      .data;
  },

  markNoShow: async (showingId: number): Promise<Showing> => {
    return (await instance.post(`/portal/v1/showings/${showingId}/no_show`))
      .data;
  },

  addFeedback: async (
    showingId: number,
    data: AddFeedbackPayload
  ): Promise<Showing> => {
    return (
      await instance.post(`/portal/v1/showings/${showingId}/feedback`, data)
    ).data;
  },

  reassignShowing: async (
    showingId: number,
    data: ReassignShowingPayload
  ): Promise<Showing> => {
    return (
      await instance.post(`/portal/v1/showings/${showingId}/reassign`, data)
    ).data;
  },

  listNotes: async (showingId: number): Promise<ShowingNote[]> => {
    return (await instance.get(`/portal/v1/showings/${showingId}/notes`)).data;
  },

  addNote: async (
    showingId: number,
    data: AddNotePayload
  ): Promise<ShowingNote> => {
    return (
      await instance.post(`/portal/v1/showings/${showingId}/add_note`, data)
    ).data;
  },

  // --- Showing Rotation ---

  getRotation: async (propertyId: string): Promise<ShowingRotationSlot[]> => {
    return (await instance.get(`/portal/v1/showing_rotation/${propertyId}`))
      .data;
  },

  reorderRotation: async (
    propertyId: string,
    partnerUserIds: number[]
  ): Promise<void> => {
    await instance.put(`/portal/v1/showing_rotation/${propertyId}/reorder`, {
      partner_user_ids: partnerUserIds
    });
  },

  setNextRotationUser: async (
    propertyId: string,
    partnerUserId: number
  ): Promise<void> => {
    await instance.post(
      `/portal/v1/showing_rotation/${propertyId}/${partnerUserId}/set_next`
    );
  }
};
